import styled, { createGlobalStyle, keyframes } from "styled-components";
import { portalDrawerAntHeaderCss } from "../../../components/molecules/Header/Header.styles";

const edgeDraw = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`;

const nodeEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.72) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const rootFloat = keyframes`
  0%,
  100% {
    transform: translateY(0) translateZ(24px);
  }
  50% {
    transform: translateY(-3px) translateZ(28px);
  }
`;

const tilePulse = keyframes`
  0%,
  100% {
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06), 0 8px 20px rgba(15, 23, 42, 0.04);
  }
  50% {
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.1), 0 14px 28px rgba(15, 23, 42, 0.06);
  }
`;

const canvasDrift = keyframes`
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  50% {
    transform: translate3d(1.4%, -1.2%, 0) scale(1.03);
  }
`;

const constellationPulse = keyframes`
  0%,
  100% {
    transform: scale(0.82);
    opacity: 0.35;
  }
  50% {
    transform: scale(1.12);
    opacity: 1;
  }
`;

const expandCuePulse = keyframes`
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
`;

export const GraphFlowGlobalStyle = createGlobalStyle`
  .relationship-graph-flow.react-flow {
    --xy-edge-stroke-default: rgba(148, 163, 184, 0.35);
    --xy-background-color: transparent;
  }

  .relationship-graph-flow .react-flow__pane {
    cursor: grab;
  }

  .relationship-graph-flow .react-flow__pane:active {
    cursor: grabbing;
  }

  .relationship-graph-flow .react-flow__viewport {
    perspective: 1400px;
  }

  .relationship-graph-flow .react-flow__node {
    pointer-events: all;
    transition: transform 720ms cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform;
  }

  .relationship-graph-flow.relationship-graph-flow--simulating .react-flow__node {
    transition: none;
  }

  .relationship-graph-flow .relation-edge-group--animate path {
    animation: ${edgeDraw} 680ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }

  .relationship-graph-flow .relation-edge-hot {
    filter: drop-shadow(0 0 6px rgba(32, 96, 234, 0.26));
  }

  .relationship-graph-flow .react-flow__handle {
    opacity: 0;
    width: 1px;
    height: 1px;
    min-width: 0;
    min-height: 0;
    border: none;
    background: transparent;
  }
`;

export const GraphCanvas = styled.div`
  position: relative;
  width: 100%;
  flex: 1 1 auto;
  height: calc(100vh - 72px);
  min-height: 520px;
  background: radial-gradient(ellipse 90% 78% at 50% 44%, #ffffff 0%, #f6f9ff 52%, #edf3fe 100%);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: -8%;
    pointer-events: none;
    background:
      radial-gradient(ellipse 58% 48% at 8% 14%, rgba(32, 96, 234, 0.14) 0%, transparent 58%),
      radial-gradient(ellipse 52% 44% at 92% 18%, rgba(0, 158, 247, 0.13) 0%, transparent 55%),
      radial-gradient(ellipse 62% 50% at 78% 92%, rgba(32, 96, 234, 0.1) 0%, transparent 58%),
      radial-gradient(ellipse 48% 42% at 18% 88%, rgba(0, 158, 247, 0.09) 0%, transparent 55%);
    animation: ${canvasDrift} 18s ease-in-out infinite;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(
      circle at 50% 38%,
      rgba(255, 255, 255, 0.72) 0%,
      rgba(15, 23, 42, 0.035) 100%
    );
    z-index: 0;
  }
`;

export const GraphFlowLayer = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
`;

export const GraphZoomControls = styled.div`
  position: absolute;
  left: 50%;
  bottom: 18px;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  border-radius: 12px;
  border: 1px solid #eceff3;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 32px rgba(15, 23, 42, 0.1);
  z-index: 3;
  backdrop-filter: blur(8px);

  button {
    width: 30px;
    height: 30px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #6b7280;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    display: grid;
    place-items: center;
    transition:
      background 0.15s ease,
      color 0.15s ease,
      transform 0.15s ease;

    &:hover {
      background: #f3f4f6;
      color: #374151;
    }

    &:active {
      transform: scale(0.94);
    }
  }

  span {
    min-width: 44px;
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    user-select: none;
  }
`;

export const RelationNodeDepthWrap = styled.div`
  transform-style: preserve-3d;
  transform: ${props => `translateZ(${props.$depthZ}px) scale(${props.$scale})`};
  opacity: ${props => (props.$dim ? 0.28 : 1)};
  filter: ${props => (props.$dim ? "saturate(0.55)" : "none")};
  transition:
    transform 520ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 220ms ease,
    filter 220ms ease;
`;

export const RelationNodeShell = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  width: ${props => (props.$wide ? "128px" : "96px")};
  transform-style: preserve-3d;

  &:disabled {
    cursor: default;
    opacity: 0.65;
  }

  &:hover:not(:disabled) .relation-node-tile {
    border-color: ${props => props.$accent || "#94a3b8"};
    transform: translateY(-3px) translateZ(10px) scale(1.06);
    box-shadow: ${props => props.$accentShadow || "0 8px 22px rgba(15, 23, 42, 0.12)"};
  }
`;

export const RelationNodeTileWrap = styled.div`
  position: relative;
  transform-style: preserve-3d;
`;

export const RelationNodeTypeDot = styled.span`
  position: absolute;
  top: -3px;
  left: -3px;
  width: 9px;
  height: 9px;
  border-radius: 999px;
  background: ${props => props.$color || "#d1d5db"};
  border: 2px solid #ffffff;
  box-shadow:
    0 0 0 1px ${props => props.$color || "#d1d5db"}33,
    0 2px 6px ${props => props.$color || "#d1d5db"}44;
`;

export const RelationNodeTile = styled.div`
  width: ${props => (props.$hero ? "52px" : "44px")};
  height: ${props => (props.$hero ? "52px" : "44px")};
  border-radius: ${props => (props.$hero ? "16px" : "12px")};
  border: ${props => (props.$isFocus || props.$isHot || props.$hero ? "2px" : "1px")} solid
    ${props =>
      props.$isFocus || props.$isHot
        ? props.$tileBorderFocus || "#9ca3af"
        : props.$isExpanded
          ? props.$tileBorder || "#d1d5db"
          : props.$tileBorder || "#e5e7eb"};
  background: ${props => props.$tileBg || "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$iconColor || "#9ca3af"};
  font-size: 18px;
  position: relative;
  transition:
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease;

  .relation-node-icon {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    display: block;
    opacity: ${props => (props.$isLoading ? 0.35 : 0.95)};
    filter: drop-shadow(0 1px 2px rgba(15, 23, 42, 0.08));
  }
  box-shadow: ${props =>
    props.$isFocus || props.$isHot || props.$hero
      ? `${props.$tileShadow || "0 4px 14px rgba(15, 23, 42, 0.12)"}, 0 2px 6px rgba(15, 23, 42, 0.06)`
      : "0 1px 2px rgba(15, 23, 42, 0.04), 0 6px 16px rgba(15, 23, 42, 0.05)"};
  animation: ${props => {
      if (props.$isLoading) {
        return tilePulse;
      }
      if (props.$isEntering) {
        return nodeEnter;
      }
      return "none";
    }}
    ${props =>
      props.$isEntering
        ? "560ms cubic-bezier(0.22, 1, 0.36, 1) both"
        : "1.2s ease-in-out infinite"};
  animation-delay: ${props => props.$enterDelay || 0}ms;
`;

export const RelationNodeLabelStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  max-width: 96px;
  min-height: 28px;
`;

export const RelationNodeTypeCaption = styled.span`
  font-size: 11px;
  line-height: 1.25;
  text-align: center;
  color: ${props => (props.$isFocus ? props.$labelColor || "#334155" : "#475569")};
  font-weight: 600;
  letter-spacing: -0.01em;
  word-break: break-word;
  display: block;
  width: 100%;
`;

export const RelationNodeRecordCaption = styled.span`
  font-size: 9px;
  line-height: 1.25;
  text-align: center;
  color: #94a3b8;
  font-weight: 500;
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RelationNodeRootCaption = styled.span`
  margin-top: 6px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$labelColor || "#475569"};
  letter-spacing: -0.01em;
  text-align: center;
`;

export const RelationExpandCue = styled.span`
  position: absolute;
  right: -6px;
  bottom: -6px;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid ${props => props.$border || "#d1d5db"};
  color: ${props => props.$color || "#475569"};
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
  animation: ${expandCuePulse} 1.8s ease-in-out infinite;
`;

export const RelationNodeStatus = styled.span`
  max-width: 96px;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(248, 250, 252, 0.92);
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: capitalize;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RelationRootWrap = styled.div`
  transform-style: preserve-3d;
  animation: ${rootFloat} 4.5s ease-in-out infinite;
  opacity: ${props => (props.$dim ? 0.28 : 1)};
  filter: ${props => (props.$dim ? "saturate(0.55)" : "none")};
  transition:
    opacity 220ms ease,
    filter 220ms ease;
`;

export const RelationNodePill = styled.div`
  padding: 7px 16px;
  border-radius: 999px;
  background: ${props =>
    props.$pillBg || "linear-gradient(165deg, rgba(124, 58, 237, 0.18) 0%, #ffffff 100%)"};
  border: 1px solid ${props => props.$pillBorder || "rgba(124, 58, 237, 0.35)"};
  color: ${props => props.$pillText || "#6d28d9"};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow:
    ${props => props.$pillShadow || "0 4px 14px rgba(124, 58, 237, 0.18)"},
    0 10px 28px rgba(15, 23, 42, 0.08);
`;

export const RelationshipGraphDrawerTheme = createGlobalStyle`
  .relationship-graph-drawer .ant-drawer-header {
    ${portalDrawerAntHeaderCss}
  }
  .relationship-graph-drawer .ant-drawer-title {
    width: 100%;
  }
`;

export const GraphDrawerBody = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

export const GraphDrawerTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  strong {
    font-size: 15px;
    font-weight: 650;
    color: #0f172a;
    letter-spacing: -0.02em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 360px;
  }
`;

export const GraphInspectorPanel = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  right: 14px;
  z-index: 3;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid ${props => props.$border || "rgba(148, 163, 184, 0.28)"};
  background: rgba(255, 255, 255, 0.82);
  box-shadow:
    ${props => props.$shadow || "0 10px 30px rgba(15, 23, 42, 0.08)"},
    0 1px 2px rgba(15, 23, 42, 0.04);
  backdrop-filter: blur(16px);
  pointer-events: auto;
`;

export const GraphInspectorIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid ${props => props.$border || "#e5e7eb"};
  background: ${props => props.$bg || "#fff"};
  display: grid;
  place-items: center;

  .relation-node-icon {
    width: 20px;
    height: 20px;
    display: block;
  }
`;

export const GraphInspectorTitle = styled.div`
  font-size: 13px;
  font-weight: 650;
  color: #0f172a;
  letter-spacing: -0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const GraphInspectorMeta = styled.div`
  margin-top: 2px;
  font-size: 11px;
  font-weight: 500;
  color: ${props => props.$color || "#64748b"};
  text-transform: capitalize;
`;

export const GraphInspectorActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

export const GraphInspectorOpen = styled.button`
  border: 1px solid ${props => props.$color || "#6366f1"}33;
  background: ${props => props.$bg || "#eef2ff"};
  color: ${props => props.$color || "#4f46e5"};
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.1);
  }
`;

export const GraphInspectorHint = styled.span`
  font-size: ${props => (props.$emphasis ? "11px" : "10px")};
  font-weight: ${props => (props.$emphasis ? 600 : 400)};
  color: ${props => (props.$emphasis ? "#2060ea" : "#94a3b8")};
  white-space: nowrap;
`;

export const GraphLegend = styled.div`
  position: absolute;
  left: 14px;
  bottom: 18px;
  z-index: 3;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 42%;
  pointer-events: none;
`;

export const GraphLegendChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  border: 1px solid ${props => props.$border || "#e5e7eb"};
  color: ${props => props.$color || "#475569"};
  font-size: 10px;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(8px);

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: ${props => props.$color || "#94a3b8"};
  }
`;

export const GraphLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: grid;
  place-items: center;
  pointer-events: none;
`;

export const GraphConstellation = styled.div`
  position: relative;
  width: 92px;
  height: 92px;

  span {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 999px;
    animation: ${constellationPulse} 1.4s ease-in-out infinite;
  }
`;

export const GraphLoadingCaption = styled.div`
  margin-top: 16px;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
`;
